import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import VendorsPage from './VendorsPage';
import { vendorCategories, vendorSlugToCategory } from '@/lib/vendorCategories';
import { categoryNameToSeoPath, resolveCategoryNameFromSeoSlug } from '@/lib/vendorCategorySeo';
import { getSeoPreviewMode } from '@/lib/seoPreviewMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEO_CATEGORY_ALIASES = {
  'wedding-venues-gibraltar': 'Ceremony & Reception Venues',
  'gibraltar-wedding-packages': 'All in One Registry Packages',
  'wedding-photographers-gibraltar': 'Photographers & Videographers',
  'wedding-planners-gibraltar': 'Wedding Planners',
  'wedding-florists-gibraltar': 'Florists',
  'wedding-hair-makeup-gibraltar': 'Hair & Beauty',
  'wedding-cakes-gibraltar': 'Cake Makers',
  'wedding-decor-gibraltar': 'Wedding Decor & Styling',
  'wedding-transport-gibraltar': 'Travel',
  'wedding-entertainment-gibraltar': 'Entertainment',
};

const SEO_ALL_SUPPLIERS_SLUGS = new Set(['wedding-suppliers-gibraltar']);

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [resolvedCategory, setResolvedCategory] = useState(null);
  const seoPreviewMode = getSeoPreviewMode(location.search);
  const isAllSuppliersAlias = SEO_ALL_SUPPLIERS_SLUGS.has(categorySlug);
  const aliasCategory = SEO_CATEGORY_ALIASES[categorySlug] || null;

  const legacyCategory = useMemo(() => vendorSlugToCategory[categorySlug], [categorySlug]);
  const legacySeoPath = useMemo(
    () => (legacyCategory ? categoryNameToSeoPath(legacyCategory) : null),
    [legacyCategory],
  );

  useEffect(() => {
    const resolveCategory = async () => {
      if (!categorySlug) {
        setResolvedCategory(null);
        setLoading(false);
        return;
      }

      if (legacyCategory) {
        setResolvedCategory(legacyCategory);
        setLoading(false);
        return;
      }

      if (aliasCategory) {
        setResolvedCategory(aliasCategory);
        setLoading(false);
        return;
      }

      if (isAllSuppliersAlias) {
        setResolvedCategory('All Categories');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/categories`);
        const categories = Array.isArray(response.data) ? response.data : [];
        const categoriesSource = seoPreviewMode
          ? categories
          : categories.filter((category) => category?.visible !== false);

        const matchedCategory =
          resolveCategoryNameFromSeoSlug(categorySlug, categoriesSource) ||
          resolveCategoryNameFromSeoSlug(categorySlug, vendorCategories);
        setResolvedCategory(matchedCategory);
      } catch {
        const fallbackMatch = resolveCategoryNameFromSeoSlug(categorySlug, vendorCategories);
        setResolvedCategory(fallbackMatch);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    resolveCategory();
  }, [aliasCategory, categorySlug, isAllSuppliersAlias, legacyCategory, seoPreviewMode]);

  if (legacySeoPath && legacySeoPath !== location.pathname) {
    return <Navigate to={legacySeoPath} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center" data-testid="category-page-loading">
        <Loader2 className="h-10 w-10 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  if (!resolvedCategory) {
    return <Navigate to="/vendors" replace />;
  }

  if (isAllSuppliersAlias) {
    return <VendorsPage seoSlug={categorySlug} isSeoLanding />;
  }

  return <VendorsPage initialCategory={resolvedCategory} seoSlug={categorySlug} isSeoLanding />;
}
